import { DOCUMENT } from "@angular/common";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { catchError, defer, finalize, firstValueFrom, from, map, Observable, of, shareReplay, throwError } from "rxjs";
import { environment } from "../../../../environments/environment";
import { UserDeviceRegistration } from "../../../models/api/userSettings";
import { AuthService } from "../auth/auth.service";

type NavigatorWithUserAgentData = Navigator & { userAgentData: { brands: { brand: string; version: string }[] } };

interface PendingDeviceRegistration {
  storageKey: string | null;
  deviceName: string;
  expectedStoredDeviceId: string | null;
  deviceId: string;
}

@Injectable({
  providedIn: "root",
})
export class UserSettingsService {
  private auth = inject(AuthService);
  private document = inject(DOCUMENT);
  private http = inject(HttpClient);
  private readonly registrationsInFlight = new Map<string | null, Observable<string>>();
  private readonly DEVICE_STORAGE_KEY_PREFIX = "deviceId";
  private readonly DEVICE_PENDING_REGISTRATION_STORAGE_KEY_PREFIX = "deviceRegistrationPending";
  private readonly DEVICE_REGISTRATION_LOCK_PREFIX = "user-device-registration";
  private readonly UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  /** Returns the locally stored ID without waiting for a backend request. */
  public getCurrentDeviceId(): string | null {
    const storageKey = this.getDeviceStorageKey();
    const storedDeviceId = this.getStoredDeviceId(storageKey);
    return this.isValidDeviceId(storedDeviceId) ? storedDeviceId : null;
  }

  public ensureCurrentDeviceRegistered(): Observable<string> {
    const storageKey = this.getDeviceStorageKey();
    return this.runRegistrationOperation(storageKey, () => this.ensureCurrentDeviceRegisteredOnce(storageKey));
  }

  public recoverCurrentDeviceRegistration(missingDeviceId: string): Observable<string> {
    const storageKey = this.getDeviceStorageKey();
    return this.runRegistrationOperation(storageKey, () =>
      this.recoverCurrentDeviceRegistrationOnce(storageKey, missingDeviceId)
    );
  }

  private runRegistrationOperation(storageKey: string | null, factory: () => Observable<string>): Observable<string> {
    const inFlight = this.registrationsInFlight.get(storageKey);
    if (inFlight) {
      return inFlight;
    }

    const operation = defer(factory).pipe(
      finalize(() => {
        if (this.registrationsInFlight.get(storageKey) === operation) {
          this.registrationsInFlight.delete(storageKey);
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.registrationsInFlight.set(storageKey, operation);
    return operation;
  }

  private ensureCurrentDeviceRegisteredOnce(storageKey: string | null): Observable<string> {
    this.assertCurrentStorageKey(storageKey);
    const storedDeviceId = this.getStoredDeviceId(storageKey);

    if (this.isValidDeviceId(storedDeviceId)) {
      return of(storedDeviceId);
    }

    if (storedDeviceId) {
      this.removeStoredDeviceIdIfUnchanged(storageKey, storedDeviceId);
    }

    const currentStoredDeviceId = this.getStoredDeviceId(storageKey);
    if (this.isValidDeviceId(currentStoredDeviceId)) {
      return of(currentStoredDeviceId);
    }

    return this.registerNewDevice(storageKey, this.getCurrentDeviceName(), currentStoredDeviceId);
  }

  private recoverCurrentDeviceRegistrationOnce(storageKey: string | null, missingDeviceId: string): Observable<string> {
    this.assertCurrentStorageKey(storageKey);
    this.removeStoredDeviceIdIfUnchanged(storageKey, missingDeviceId);
    const currentStoredDeviceId = this.getStoredDeviceId(storageKey);
    if (this.isValidDeviceId(currentStoredDeviceId)) {
      return of(currentStoredDeviceId);
    }

    return this.registerNewDevice(storageKey, this.getCurrentDeviceName(), currentStoredDeviceId);
  }

  private registerNewDevice(
    storageKey: string | null,
    deviceName: string,
    expectedStoredDeviceId: string | null
  ): Observable<string> {
    const lockManager = this.document.defaultView?.navigator.locks;
    if (!storageKey || !lockManager) {
      return this.registerNewDeviceWithoutLock(storageKey, deviceName, expectedStoredDeviceId);
    }

    return defer(() => {
      // lib.dom models the callback result literally; Promise.resolve flattens
      // the asynchronous registration result to the observable's string value.
      const registration = Promise.resolve(
        lockManager.request(this.getDeviceRegistrationLockName(storageKey), async () => {
          this.assertCurrentStorageKey(storageKey);
          const currentStoredDeviceId = this.getStoredDeviceId(storageKey);
          if (this.isValidDeviceId(currentStoredDeviceId)) {
            return currentStoredDeviceId;
          }

          return firstValueFrom(this.registerNewDeviceWithoutLock(storageKey, deviceName, currentStoredDeviceId));
        })
      );
      return from(registration);
    });
  }

  private registerNewDeviceWithoutLock(
    storageKey: string | null,
    deviceName: string,
    expectedStoredDeviceId: string | null
  ): Observable<string> {
    return defer(() => {
      this.assertCurrentStorageKey(storageKey);
      const deviceId = this.getOrCreatePendingDeviceId(storageKey);

      return this.registerPendingDevice({ storageKey, deviceName, expectedStoredDeviceId, deviceId }, true);
    });
  }

  private registerPendingDevice(
    registration: PendingDeviceRegistration,
    allowRegistrationRequiredRetry: boolean
  ): Observable<string> {
    return defer(() => {
      this.assertCurrentStorageKey(registration.storageKey);
      return this.registerDevice({
        id: registration.deviceId,
        pushToken: null,
        name: registration.deviceName,
        platform: "browser",
      });
    }).pipe(
      map(() => this.promotePendingDevice(registration)),
      catchError((error: unknown) => {
        if (this.deviceAlreadyRegistered(error)) {
          return this.updatePendingDevice(registration).pipe(
            map(() => this.promotePendingDevice(registration)),
            catchError((updateError: unknown) =>
              this.retryWithReplacementDevice(registration, allowRegistrationRequiredRetry, updateError)
            )
          );
        }

        return this.retryWithReplacementDevice(registration, allowRegistrationRequiredRetry, error);
      })
    );
  }

  private updatePendingDevice(registration: PendingDeviceRegistration): Observable<void> {
    return defer(() => {
      this.assertCurrentStorageKey(registration.storageKey);
      return this.http.put<void>(`${environment.baseUrl}/me/device/${registration.deviceId}`, {
        pushToken: null,
        name: registration.deviceName,
      });
    });
  }

  private retryWithReplacementDevice(
    registration: PendingDeviceRegistration,
    allowRegistrationRequiredRetry: boolean,
    error: unknown
  ): Observable<string> {
    if (!allowRegistrationRequiredRetry || !this.requiresDeviceRegistration(error)) {
      return throwError(() => error);
    }

    this.assertCurrentStorageKey(registration.storageKey);
    const replacementDeviceId = this.replacePendingDeviceIdIfUnchanged(registration.storageKey, registration.deviceId);
    if (!replacementDeviceId) {
      return throwError(() => error);
    }

    return this.registerPendingDevice({ ...registration, deviceId: replacementDeviceId }, false);
  }

  private getOrCreatePendingDeviceId(storageKey: string | null): string {
    if (!storageKey) {
      return crypto.randomUUID();
    }

    const pendingStorageKey = this.getPendingDeviceStorageKey(storageKey);
    const pendingDeviceId = this.localStorage?.getItem(pendingStorageKey) ?? null;
    if (this.isValidDeviceId(pendingDeviceId)) {
      return pendingDeviceId;
    }

    const deviceId = crypto.randomUUID();
    this.localStorage?.setItem(pendingStorageKey, deviceId);
    return deviceId;
  }

  private removePendingDeviceIdIfUnchanged(storageKey: string | null, expectedDeviceId: string): void {
    if (!storageKey) {
      return;
    }

    const pendingStorageKey = this.getPendingDeviceStorageKey(storageKey);
    if (this.localStorage?.getItem(pendingStorageKey) === expectedDeviceId) {
      this.localStorage.removeItem(pendingStorageKey);
    }
  }

  private replacePendingDeviceIdIfUnchanged(storageKey: string | null, expectedDeviceId: string): string | null {
    if (!storageKey) {
      return crypto.randomUUID();
    }

    const pendingStorageKey = this.getPendingDeviceStorageKey(storageKey);
    if (this.localStorage?.getItem(pendingStorageKey) !== expectedDeviceId) {
      return null;
    }

    const replacementDeviceId = crypto.randomUUID();
    this.localStorage.setItem(pendingStorageKey, replacementDeviceId);
    return replacementDeviceId;
  }

  private getPendingDeviceStorageKey(storageKey: string): string {
    return `${this.DEVICE_PENDING_REGISTRATION_STORAGE_KEY_PREFIX}.${storageKey}`;
  }

  private getDeviceRegistrationLockName(storageKey: string): string {
    return `${this.DEVICE_REGISTRATION_LOCK_PREFIX}.${storageKey}`;
  }

  private promotePendingDevice(registration: PendingDeviceRegistration): string {
    this.assertCurrentStorageKey(registration.storageKey);
    if (registration.storageKey) {
      const pendingDeviceId =
        this.localStorage?.getItem(this.getPendingDeviceStorageKey(registration.storageKey)) ?? null;
      if (pendingDeviceId !== registration.deviceId) {
        const currentStoredDeviceId = this.getStoredDeviceId(registration.storageKey);
        if (this.isValidDeviceId(currentStoredDeviceId)) {
          return currentStoredDeviceId;
        }

        throw new Error("Pending device changed during device registration");
      }
    }

    const storedDeviceId = this.storeRegisteredDeviceId(
      registration.storageKey,
      registration.expectedStoredDeviceId,
      registration.deviceId
    );
    this.removePendingDeviceIdIfUnchanged(registration.storageKey, registration.deviceId);
    return storedDeviceId;
  }

  private storeRegisteredDeviceId(
    storageKey: string | null,
    expectedStoredDeviceId: string | null,
    registeredDeviceId: string
  ): string {
    this.assertCurrentStorageKey(storageKey);
    if (!storageKey) {
      return registeredDeviceId;
    }

    const currentStoredDeviceId = this.localStorage?.getItem(storageKey) ?? null;
    if (currentStoredDeviceId === expectedStoredDeviceId || !this.isValidDeviceId(currentStoredDeviceId)) {
      this.localStorage?.setItem(storageKey, registeredDeviceId);
      return registeredDeviceId;
    }

    return currentStoredDeviceId;
  }

  private removeStoredDeviceIdIfUnchanged(storageKey: string | null, expectedDeviceId: string): void {
    if (!storageKey) {
      return;
    }

    if (this.localStorage?.getItem(storageKey) === expectedDeviceId) {
      this.localStorage.removeItem(storageKey);
    }
  }

  private get localStorage(): Storage | undefined {
    return this.document.defaultView?.localStorage;
  }

  private getStoredDeviceId(storageKey: string | null): string | null {
    return storageKey ? (this.localStorage?.getItem(storageKey) ?? null) : null;
  }

  private isValidDeviceId(value: string | null): value is string {
    return value !== null && this.UUID_V4_PATTERN.test(value);
  }

  private requiresDeviceRegistration(error: unknown): error is HttpErrorResponse {
    return this.hasHttpErrorCode(error, 412, "USER_DEVICE_REGISTRATION_REQUIRED");
  }

  private deviceAlreadyRegistered(error: unknown): error is HttpErrorResponse {
    return this.hasHttpErrorCode(error, 409, "USER_DEVICE_ALREADY_REGISTERED");
  }

  private hasHttpErrorCode(error: unknown, status: number, expectedCode: string): error is HttpErrorResponse {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    const code =
      typeof error.error === "object" && error.error !== null && "code" in error.error
        ? (error.error as { code?: unknown }).code
        : undefined;
    return error.status === status && code === expectedCode;
  }

  private assertCurrentStorageKey(expectedStorageKey: string | null): void {
    if (this.getDeviceStorageKey() !== expectedStorageKey) {
      throw new Error("Authenticated user changed during device registration");
    }
  }

  private registerDevice(device: UserDeviceRegistration): Observable<void> {
    return this.http.post<void>(`${environment.baseUrl}/me/device`, device);
  }

  private getDeviceStorageKey(): string | null {
    const token = this.auth.getToken();
    if (!token) {
      return null;
    }

    try {
      const { id: userId } = JSON.parse(atob(token.split(".")[1])) as { id?: string };
      return userId ? `${this.DEVICE_STORAGE_KEY_PREFIX}.${userId}` : null;
    } catch {
      return null;
    }
  }

  private getBrowserName(navigator?: Navigator): string | null {
    if (!navigator) {
      return null;
    }

    if ("userAgentData" in navigator) {
      const navigatorWithUserAgentData = navigator as NavigatorWithUserAgentData;
      const brand = navigatorWithUserAgentData.userAgentData.brands.find(
        (b) => !b.brand.includes("Not") && b.brand !== "Chromium"
      );
      if (brand) {
        return brand.brand;
      }
    }

    const ua = navigator.userAgent;
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Edg/")) return "Edge";
    if (ua.includes("Chrome")) return "Chrome";
    if (ua.includes("Safari")) return "Safari";
    if (ua.includes("Opera") || ua.includes("OPR")) return "Opera";

    return "Unknown Browser";
  }

  private getCurrentDeviceName(): string {
    const navigator = this.document.defaultView?.navigator;
    return `${this.getBrowserName(navigator) ?? "Unknown Browser"} | ${navigator?.platform ?? "Unknown OS"}`;
  }
}
